import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import { cn } from "../../../lib/helpers";
import pipelineHeroQuizData from "./pipeline-hero-quiz.json";
import { QuizResults } from "./quiz-results";
import { freeEmailDomains } from "../../../../static/data/free-email-domains";
import { QuizSectionHeader } from "./quiz-blocks";
import { ProgressBar } from "./progress-bar";
import { AnswerButton } from "./AnswerButton";
import { SiteContext } from "../../global/site-context";

export const WORKFLOW_LOOKUP_ID = "89991fe9-319a-4169-9bcc-34fd909568b5";
export const WORKFLOW_QUIZ_ID = "fbc99331-2d0f-4985-a8f5-1ab7e6500545";

// Fetch cached results from the hash ID
const fetchCachedResults = async (hash, buildToken, setQuizResult, setStep) => {
  try {
    const response = await fetch("/.netlify/functions/airops", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        inputs: {
          hash: hash,
          workflowId: WORKFLOW_QUIZ_ID,
          token: buildToken
        }
      })
    });

    if (response.ok) {
      const data = await response.json();
      console.log('Cached results found:', data.result);

      if (data.result) {
        // Parse the result if it's a string
        let parsedResult = data.result;
        if (typeof data.result === 'string') {
          try {
            parsedResult = JSON.parse(data.result);
          } catch (parseError) {
            console.error('Failed to parse cached result:', parseError);
            return;
          }
        }

        // Set the quiz result and skip to results page
        setQuizResult(parsedResult);
        setStep(6);
      }
    } else {
      console.error('Failed to fetch cached results:', response.statusText);
    }
  } catch (error) {
    console.error('Error fetching cached results:', error);
  }
};

export const Quiz = ({ section }) => {
  const [step, setStep] = useState(1);
  const [persona, setPersona] = useState(null);
  const [role, setRole] = useState(null);
  const [email, setEmail] = useState("");
  const [_emailValid, setEmailValid] = useState(true);
  const [emailError, setEmailError] = useState("");
  const [answers, setAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [hasAutoAdvanced, setHasAutoAdvanced] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [_transitionDirection, setTransitionDirection] = useState('next');
  const [companyLookupData, setCompanyLookupData] = useState(null);
  const [_companyLookupLoading, setCompanyLookupLoading] = useState(false);
  const [_companyLookupError, setCompanyLookupError] = useState(null);
  const [hasTriggeredCompanyLookup, setHasTriggeredCompanyLookup] = useState(false);

  // Generate unique session hash for this quiz session
  const [sessionHash] = useState(() => {
    return Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15) +
      Date.now().toString(36);
  });

  const site = useContext(SiteContext);

  // Check for "results" param on load and skip quiz if present
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const urlParams = new URLSearchParams(window.location.search);
    const resultsHash = urlParams.get('results');

    if (resultsHash && resultsHash.trim() !== '') {
      // Fetch cached results for this hash
      fetchCachedResults(resultsHash, site.buildToken, setQuizResult, setStep);
    }
  }, [site.buildToken]);

  // Track screen size for animation control
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  // Check screen size on mount and resize
  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < 640); // 640px is Tailwind's "sm" breakpoint
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const personaQuestion = pipelineHeroQuizData.personaQuestion;

  // Animated step change function
  const changeStep = (newStep, direction = 'next') => {
    if (newStep === step || isTransitioning) return;

    // Skip animation on mobile screens
    if (isSmallScreen) {
      setStep(newStep);
      return;
    }

    setIsTransitioning(true);
    setTransitionDirection(direction);

    // Change step immediately and let CSS handle the smooth transition
    setStep(newStep);

    // Reset transition state after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  };

  // Get inline style for step container (carousel-style)
  const getStepContainerStyle = () => {
    return {
      transform: `translateX(-${(step - 1) * 100}%)`,
      transition: isSmallScreen ? 'none' : 'transform 300ms ease-in-out'
    };
  };

  // Get CSS classes for individual step content
  const getStepTransitionClasses = () => {
    return "w-full flex-shrink-0";
  };

  // Get persona-specific questions
  const getQuestionsForPersona = (selectedPersona) => {
    if (!selectedPersona || !pipelineHeroQuizData.questionsByPersona[selectedPersona]) {
      return [];
    }

    // Return the flat array of questions for the selected persona
    return pipelineHeroQuizData.questionsByPersona[selectedPersona];
  };

  // Memoize questions for persona to avoid recalculation
  const currentQuestions = useMemo(() => {
    return persona ? getQuestionsForPersona(persona) : [];
  }, [persona]);

  // Memoized function to get questions by hero type
  const getQuestionsByHero = useCallback((selectedPersona, heroType) => {
    if (!selectedPersona || !pipelineHeroQuizData.questionsByPersona[selectedPersona]) {
      return [];
    }
    return pipelineHeroQuizData.questionsByPersona[selectedPersona].filter(q => q.hero === heroType);
  }, []);

  // Memoize current hero questions to avoid recalculation on every render
  const currentHeroQuestions = useMemo(() => {
    if (!persona) return [];

    switch (step) {
      case 3: return getQuestionsByHero(persona, 'oracle');
      case 4: return getQuestionsByHero(persona, 'striker');
      case 5: return getQuestionsByHero(persona, 'resonator');
      default: return [];
    }
  }, [persona, step, getQuestionsByHero]);

  // Email validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isDomainFree = (domain) => {
    if (!domain) return false;
    return freeEmailDomains.includes(domain.toLowerCase());
  };

  // Handle email validation
  const handleEmailValidation = () => {
    if (!email) {
      setEmailError("Email is required");
      setEmailValid(false);
      return false;
    }

    if (!validateEmail(email)) {
      setEmailError("Invalid email format");
      setEmailValid(false);
      return false;
    }

    const domain = email.split('@')[1]?.toLowerCase();
    if (isDomainFree(domain)) {
      setEmailError("Use a business email from your company domain");
      setEmailValid(false);
      return false;
    }

    setEmailError("");
    setEmailValid(true);
    return true;
  };

  const handleEmailBlur = () => {
    if (email) {
      handleEmailValidation();
    }
  };

  const handleStartClick = () => {
    if (handleEmailValidation()) {
      changeStep(3, 'next'); // Move to first hero quiz step (oracle) with animation
      // Trigger company lookup when moving to step 3
      if (!hasTriggeredCompanyLookup) {
        // email capture metric once
        site.metrics.logClick("hero-quiz", "email", email);
        setTimeout(() => fetchCompanyLookup(), 100);
      }
    }
  };

  // Fetch company lookup data from AirOps
  const fetchCompanyLookup = async () => {
    if (!email || !persona || !role || hasTriggeredCompanyLookup) return;

    setCompanyLookupLoading(true);
    setCompanyLookupError(null);
    setHasTriggeredCompanyLookup(true);

    try {
      const domain = email.split('@')[1];
      const response = await fetch("/.netlify/functions/airops", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          inputs: {
            domain: domain,
            email: email,
            dept: persona,
            role: role === "individual_contributor" ? "Individual Contributor" : "Leader",
            hash: sessionHash,
            workflowId: WORKFLOW_LOOKUP_ID,
            token: site.buildToken
          },
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Company lookup response:", data);
        setCompanyLookupData(data.result || null);

      } else {
        console.error('Company lookup failed:', response.statusText);
        setCompanyLookupError('Failed to fetch company data');
      }
    } catch (error) {
      console.error('Error fetching company lookup:', error);
      setCompanyLookupError('Connection error during company lookup');
    } finally {
      setCompanyLookupLoading(false);
    }
  };

  // Auto-advance to step 2 when persona and role are selected (only once)
  useEffect(() => {
    if (persona && role && step === 1 && !hasAutoAdvanced && !isSmallScreen) {
      changeStep(2, 'next'); // Move to email step with animation
      setHasAutoAdvanced(true); // Mark that auto-advance has happened
    }
  }, [persona, role, step, hasAutoAdvanced, isSmallScreen]);

  // Optimized header text color function
  const getHeaderTextColor = useCallback((optionValue) => {
    switch (optionValue) {
      case 1: return "text-superhero-nope";
      case 2: return "text-superhero-maybe";
      case 3: return "text-superhero-agree";
      case 4: return "text-superhero-link";
      default: return "text-light-80";
    }
  }, []);

  const calculateResult = (answersToUse = answers) => {
    // New simplified scoring system
    const heroScores = {
      oracle: 0,
      striker: 0,
      resonator: 0
    };

    const quizAnswers = [];

    currentQuestions.forEach((question) => {
      const selectedValue = answersToUse[question.id];
      if (selectedValue !== undefined && question.hero) {
        // Convert answer value (1-4) to score (0-3)
        const score = selectedValue - 1;

        // Add score to the appropriate hero
        heroScores[question.hero] += score;

        // Store question data for final output
        quizAnswers.push({
          question: question.question,
          context: question.context,
          hero: question.hero,
          score: score
        });
      }
    });

    // Calculate maximum possible score for each hero
    const heroQuestionCounts = {
      oracle: 0,
      striker: 0,
      resonator: 0
    };

    currentQuestions.forEach(question => {
      if (question.hero && heroQuestionCounts.hasOwnProperty(question.hero)) {
        heroQuestionCounts[question.hero]++;
      }
    });

    // Create matches array with fraction scores
    const matches = Object.keys(heroScores).map(heroKey => {
      const score = heroScores[heroKey];
      const maxScore = heroQuestionCounts[heroKey] * 3; // 3 is max score per question
      const archetype = pipelineHeroQuizData.archetypes[heroKey];

      return {
        name: archetype ? archetype.name : heroKey,
        score: `${score}/${maxScore}`
      };
    });

    // Find the best match
    const bestMatchKey = Object.keys(heroScores).reduce((a, b) =>
      heroScores[a] > heroScores[b] ? a : b
    );

    // Create the result in the new format
    const result = {
      dept: persona.charAt(0).toUpperCase() + persona.slice(1), // Capitalize first letter
      role: role === "individual_contributor" ? "Individual Contributor" : "Leader",
      email: email,
      matches: matches,
      quiz: quizAnswers,
      // Include company lookup data if available
      companyData: companyLookupData,
      // Keep legacy data for UI compatibility
      ...pipelineHeroQuizData.archetypes[bestMatchKey],
      allScores: heroScores
    };

    console.log('New quiz result:', result);
    return result;
  };

  const handleAnswer = useCallback((questionIndex, answerValue) => {
    // Create a unique key for this question based on its actual ID
    const question = currentHeroQuestions[questionIndex];
    const questionKey = question ? question.id : questionIndex;

    setAnswers(prev => ({
      ...prev,
      [questionKey]: answerValue
    }));
  }, [currentHeroQuestions]);

  // Use useEffect to handle auto-advancement when answers change
  useEffect(() => {
    if (currentHeroQuestions.length === 0 || isSmallScreen) return;

    const allCurrentQuestionsAnswered = currentHeroQuestions.every(q => answers[q.id] !== undefined);

    if (allCurrentQuestionsAnswered) {
      // Small delay to ensure smooth UI updates before transition
      const timeoutId = setTimeout(() => {
        if (step === 3) {
          changeStep(4, 'next'); // Oracle → Striker with animation
        } else if (step === 4) {
          changeStep(5, 'next'); // Striker → Resonator with animation
        }
        // Don't auto-advance from step 5 - let them click "Get My Results"
      }, 150);

      return () => clearTimeout(timeoutId);
    }
  }, [answers, currentHeroQuestions, step, isSmallScreen]);

  // Memoized check if current hero questions are answered
  const areCurrentQuestionsAnswered = useMemo(() => {
    return currentHeroQuestions.every(question => answers[question.id] !== undefined);
  }, [currentHeroQuestions, answers]);

  const handleNextHeroStep = () => {
    if (step === 3) {
      changeStep(4, 'next'); // Oracle → Flashpoint with animation
    } else if (step === 4) {
      changeStep(5, 'next'); // Flashpoint → Resonator with animation
    } else if (step === 5) {
      // All heroes done, calculate results
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    const result = calculateResult();
    console.log('Quiz result scores:', result.allScores);
    setQuizResult(result);
    changeStep(6, 'next'); // Move to results step with animation
    site.metrics.logClick("hero-quiz", "results-started");
  };

  const handlePersonaSelection = (personaValue) => {
    // If changing persona, clear existing answers since questions may be different
    if (persona && persona !== personaValue) {
      setAnswers({});
    }
    setPersona(personaValue);
    site.metrics.logClick("hero-quiz", "started", personaValue);
  };

  const handleRoleSelection = (roleValue) => {
    setRole(roleValue);
  };

  const resetToStart = () => {
    setStep(1);
    setPersona(null);
    setRole(null);
    setEmail("");
    setEmailValid(true);
    setEmailError("");
    setAnswers({});
    setQuizResult(null);
    setHasAutoAdvanced(false);
    setIsTransitioning(false);
    setTransitionDirection('next');
  };

  // If a persona is selected but no questions are available
  if (persona && (!currentQuestions || currentQuestions.length === 0)) {
    return (
      <div className="text-center p-8">
        <p className="text-gray-600">No questions configured for the {persona} persona.</p>
      </div>
    );
  }

  if (step === 6) {
    return (
      <QuizResults
        quizResult={quizResult}
        sessionHash={sessionHash}
        onReset={resetToStart}
      />
    );
  }

  return (
    <>
      <QuizSectionHeader
        bgTitle="The Quiz"
        title="Unlock your GTM Superpowers"
        summary="See your strengths. Identify your gaps. Get a tailor-made strengths-to-superpowers transformation plan."
      />
      <div
        id="quiz"
        style={{
          minHeight: "500px",
          WebkitOverflowScrolling: "touch",
          WebkitTapHighlightColor: "transparent"
        }}
        className="overflow-hidden flex flex-col justify-between bg-gray-1000 border border-light-10 font-openSans rounded-2xl max-w-6xl mx-auto p-4 sm:p-6 lg:p-8 lg:px-24"
      >
        <div className="flex items-center justify-between mb-4 gap-12">
          <h3 className={cn(
            "text-2xl text-light-60 font-superhero font-semibold italic uppercase pr-1",
            step === 1 ? "opacity-30" : "opacity-100"
          )}>Step {step}</h3>
          <ProgressBar step={step} className="md:hidden" />
        </div>

        {/* Carousel Container */}
        <div className="overflow-hidden" style={{ minHeight: "14rem" }}>
          <div
            className="flex w-full"
            style={getStepContainerStyle()}
          >
            {/* Step 1: Persona and Role Selection */}
            <div className={getStepTransitionClasses()}>
              {/* Persona Selection */}
              <div className="">
                <h2 className="text-lg text-white mb-4">What's your <span className="font-semibold">team?</span></h2>
                <div className="flex flex-col sm:flex-row w-full gap-6 items-center mb-12">
                  {/* SELECT Team */}
                  {personaQuestion?.options?.map((option) => (
                    <button
                      key={option.value}
                      onTouchStart={isSmallScreen ? (e) => {
                        e.currentTarget.style.transform = 'scale(0.98)';
                      } : undefined}
                      onTouchEnd={isSmallScreen ? (e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        handlePersonaSelection(option.value);
                      } : undefined}
                      onClick={!isSmallScreen ? () => handlePersonaSelection(option.value) : undefined}
                      className={cn(
                        "flex-1 px-10 py-3 w-full rounded-xl font-medium text-xl transition-colors duration-200 outline-none ring-0 focus:ring-0 focus:outline-none touch-manipulation select-none",
                        persona === option.value
                          ? "bg-superhero-link text-black"
                          : "border border-link text-link hover:text-white hover:border-white",
                        isSmallScreen && "active:scale-98"
                      )}
                      style={{ WebkitTapHighlightColor: 'transparent', willChange: isSmallScreen ? 'transform' : undefined }}
                    >
                      {option.text}
                    </button>
                  ))}
                </div>

                <h2 className="text-lg text-white mb-4">What's your <span className="font-semibold">role?</span></h2>
                <div className="flex flex-col sm:flex-row gap-6 items-center mb-12">
                  {/* SELECT Role */}
                  {[
                    { value: "individual_contributor", label: "Individual Contributor" },
                    { value: "leader", label: "Leader" },
                  ].map((roleOption) => (
                    <button
                      key={roleOption.value}
                      onTouchStart={isSmallScreen ? (e) => {
                        e.currentTarget.style.transform = 'scale(0.98)';
                      } : undefined}
                      onTouchEnd={isSmallScreen ? (e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        handleRoleSelection(roleOption.value);
                      } : undefined}
                      onClick={!isSmallScreen ? () => handleRoleSelection(roleOption.value) : undefined}
                      className={cn(
                        "flex-1 px-10 py-3 w-full rounded-xl font-medium text-xl transition-colors duration-200 outline-none ring-0 focus:ring-0 focus:outline-none touch-manipulation select-none",
                        role === roleOption.value
                          ? "bg-superhero-link text-black"
                          : "border border-link text-link hover:text-white hover:border-white",
                        isSmallScreen && "active:scale-98"
                      )}
                      style={{ WebkitTapHighlightColor: 'transparent', willChange: isSmallScreen ? 'transform' : undefined }}
                    >
                      {roleOption.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Step 2: Email Collection */}
            <div className={cn("flex items-center", getStepTransitionClasses())}>
              <div>
                <h2 className="text-2xl mb-2 text-white">
                  What's your <span className="font-semibold">work email?</span>
                </h2>
                <p className="mt-2 mb-12 opacity-60">
                  Our AI will tailor results with your organization's domain.
                </p>
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailError) {
                        setEmailError("");
                        setEmailValid(true);
                      }
                    }}
                    onBlur={handleEmailBlur}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleStartClick();
                      }
                    }}
                    className={cn(
                      "w-full p-4 rounded-xl text-lg transition-all duration-200 bg-black text-white border-4 outline-none focus:ring-0 max-w-md",
                      emailError ? "border-red" : "border-link"
                    )}
                    placeholder="email@company.com"
                    style={{ colorScheme: "dark" }}
                    autoComplete="email"
                    required
                  />
                  <p className="opacity-40 mt-6 text-sm max-w-sm leading-snug">
                    By providing your email, you agree to receive marketing emails per our privacy policy. Unsubscribe anytime.
                  </p>
                </div>

                {emailError && (
                  <div className="my-6">
                    <p className="text-red">{emailError}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Steps 3-5: Individual Hero Questions */}
            {[3, 4, 5].map((stepNum) => (
              <div key={stepNum} className={getStepTransitionClasses()}>
                {step === stepNum && (
                  <div>
                    {/* Sticky Header with Answer Scale - Only visible on md+ */}
                    <div className="hidden md:block">
                      {/* Desktop header */}
                      <div className="grid grid-cols-8 gap-4 items-center">
                        <div className="text-lg font-semibold text-transparent col-span-4">
                          Questions
                        </div>
                        {pipelineHeroQuizData.questionScale?.map((option, index) => (
                          <div key={index} className="text-center text-sm">
                            <div className={getHeaderTextColor(option.value)}>
                              {option.text}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Hero-specific questions */}
                    <div className="space-y-2 mb-8">
                      {currentHeroQuestions.map((question, questionIndex) => (
                        <div key={question.id} className="border-b border-dark-10 rounded-lg pt-4 pb-4 relative">
                          <span className="absolute -top-1 left-0 text-sm opacity-50 text-white">
                            #{questionIndex + 1}
                          </span>

                          {/* Desktop layout */}
                          <div className="hidden md:grid grid-cols-8 gap-4 items-center">
                            {/* Question on the left - takes up half the width */}
                            <div className="col-span-4">
                              <h3 className="text-xl">
                                {question.question}
                              </h3>
                            </div>

                            {/* Answer options - one per column, sharing the right half */}
                            {pipelineHeroQuizData.questionScale?.map((option, optionIndex) => (
                              <AnswerButton
                                key={optionIndex}
                                option={option}
                                questionId={question.id}
                                isSelected={answers[question.id] === option.value}
                                onChange={(value) => handleAnswer(questionIndex, value)}
                                isMobile={false}
                              />
                            ))}
                          </div>

                          {/* Mobile layout */}
                          <div className="md:hidden space-y-4">
                            {/* Question on top */}
                            <div>
                              <h3 className="text-lg font-medium">
                                {question.question}
                              </h3>
                            </div>

                            {/* Answer options below in a row */}
                            <div className="grid grid-cols-4 gap-2">
                              {pipelineHeroQuizData.questionScale?.map((option, optionIndex) => (
                                <AnswerButton
                                  key={optionIndex}
                                  option={option}
                                  questionId={question.id}
                                  isSelected={answers[question.id] === option.value}
                                  onChange={(value) => handleAnswer(questionIndex, value)}
                                  isMobile={true}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Unified Progress indicator with navigation for steps 1-5 */}
        {step <= 5 && (
          <div className="flex items-center justify-between w-full gap-4 lg:gap-12 lg:mt-8">
            <ProgressBar step={step} className="hidden md:flex" />

            {/* Back button */}
            <div>
              <button
                onClick={() => {
                  if (step === 2) {
                    changeStep(1, 'back');
                  } else if (step === 3) {
                    changeStep(2, 'back');
                  } else {
                    changeStep(step - 1, 'back');
                  }
                }}
                disabled={step === 1}
                className={cn(
                  "px-4 lg:px-12 py-2.5 bg-gray-900 rounded-lg font-semibold text-lg transition-colors text-light-60 hover:text-white border border-light-10 hover:border-white active:scale-95 touch-manipulation",
                  step === 1 ? "opacity-10 cursor-not-allowed" : "opacity-100"
                )}
              >
                Back
              </button>
            </div>
            {/* Next button or Form submission */}
            <div>
              {(step === 2 || step === 5) ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    step === 2 ? handleStartClick() : handleSubmit();
                  }}
                  id={step === 2 ? "hero-quiz" : "hero-quiz-started"}
                >
                  {/* Hidden form fields */}
                  <div className="sr-only">
                    <input type="email" name="email" value={email} readOnly />
                    <input type="text" name="jobtitle" value={persona || ''} readOnly />
                    <input type="text" name="role" value={
                      persona === "sales" ? "Sales"
                        : persona === "marketing" ? "Marketing"
                          : persona === "revops" ? "Revenue Operations" : ''
                    } readOnly />
                    <input type="text" name="hash" value={sessionHash} readOnly />
                    <input type="text" name="seniority" value={
                      role === "individual_contributor" ? "Individual Contributor"
                        : role === "leader" ? "Leader" : ''
                    } readOnly />
                  </div>

                  <button
                    type="submit"
                    disabled={step === 2 ? (!email || emailError) : !areCurrentQuestionsAnswered}
                    className={cn(
                      "py-2.5 rounded-lg font-semibold text-lg transition-colors active:scale-95 touch-manipulation whitespace-nowrap",
                      step === 2 ? "px-8" : "px-6",
                      (step === 2 ? (!email || emailError) : !areCurrentQuestionsAnswered)
                        ? "bg-gray-800 text-gray-400 cursor-not-allowed"
                        : "bg-green-emerald text-black hover:bg-blue-700"
                    )}
                  >
                    {step === 2 ? "Next" : "Get My Results"}
                  </button>
                </form>
              ) : (
                <button
                  onClick={step === 1 ? () => changeStep(2, 'next') : handleNextHeroStep}
                  disabled={
                    step === 1 ? (!persona || !role) :
                      !areCurrentQuestionsAnswered
                  }
                  className={cn(
                    "px-8 py-2.5 rounded-lg font-semibold text-lg transition-colors active:scale-95 touch-manipulation whitespace-nowrap",
                    (step === 1 ? (!persona || !role) : !areCurrentQuestionsAnswered)
                      ? "bg-gray-800 text-gray-400 cursor-not-allowed"
                      : "bg-green-emerald text-black hover:bg-blue-700"
                  )}
                >
                  Next
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      <p className="text-light-30 hover:text-light-50 transition-colors duration-500 text-sm text-center mt-12 mb-36">
        Powered by AI magic from Claude, Gemini, and ChatGPT.
      </p>
    </>
  );
};
